import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import * as DelegationActions from './delegation.actions';
import * as DelegationSelectors from './delegation.selectors';
import { DelegationService } from '../../../core/services/gse/delegation.service';
import { ToastService } from '../../../core/services/toast.service';

@Injectable()
export class DelegationEffects {
  private actions$ = inject(Actions);
  private delegationService = inject(DelegationService);
  private toastService = inject(ToastService);
  private store = inject(Store);

  loadDelegationRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DelegationActions.loadDelegationRequests),
      switchMap(({ params }) =>
        this.delegationService.getAllDelegateRequests(params).pipe(
          map((response) =>
            DelegationActions.loadDelegationRequestsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || (response.data?.length || 0),
            })
          ),
          catchError((error) =>
            of(
              DelegationActions.loadDelegationRequestsFailure({
                error: error?.error?.message || error?.message || 'Failed to load delegation requests',
              })
            )
          )
        )
      )
    )
  );

  createDelegationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DelegationActions.createDelegationRequest),
      switchMap(({ params }) =>
        this.delegationService.createUserDelegationRequest(params).pipe(
          map((response) => DelegationActions.createDelegationRequestSuccess({ response })),
          catchError((error) =>
            of(
              DelegationActions.createDelegationRequestFailure({
                error: error?.error?.message || error?.message || 'Failed to create delegation request',
              })
            )
          )
        )
      )
    )
  );

  createDelegationRequestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DelegationActions.createDelegationRequestSuccess),
        tap(({ response }) => {
          this.toastService.showSuccess(
            'Success',
            response?.message || 'Delegation request created successfully'
          );
        })
      ),
    { dispatch: false }
  );

  createDelegationRequestFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DelegationActions.createDelegationRequestFailure),
        tap(({ error }) => {
          this.toastService.showError('Error', error);
        })
      ),
    { dispatch: false }
  );

  updateDelegationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DelegationActions.updateDelegationStatus),
      switchMap(({ params }) =>
        this.delegationService.updateDelegationStatusById(params).pipe(
          map((response) => DelegationActions.updateDelegationStatusSuccess({ response })),
          catchError((error) =>
            of(
              DelegationActions.updateDelegationStatusFailure({
                error: error?.error?.message || error?.message || 'Failed to update delegation status',
              })
            )
          )
        )
      )
    )
  );

  updateDelegationStatusRefreshList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DelegationActions.updateDelegationStatusSuccess),
      withLatestFrom(this.store.select(DelegationSelectors.selectDelegationLastLoadParams)),
      map(([, lastLoadParams]) =>
        DelegationActions.loadDelegationRequests({
          params: lastLoadParams ?? {
            page: 0,
            size: 10,
            sort: 'id,desc',
          },
        })
      )
    )
  );

  updateDelegationStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DelegationActions.updateDelegationStatusSuccess),
        tap(({ response }) => {
          this.toastService.showSuccess(
            'Success',
            response?.message || 'Delegation status updated successfully'
          );
        })
      ),
    { dispatch: false }
  );

  updateDelegationStatusFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DelegationActions.updateDelegationStatusFailure),
        tap(({ error }) => {
          this.toastService.showError('Error', error);
        })
      ),
    { dispatch: false }
  );

  loadDelegationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DelegationActions.loadDelegationById),
      switchMap(({ params }) =>
        this.delegationService.getDelegationById(params).pipe(
          map((response) => DelegationActions.loadDelegationByIdSuccess({ response })),
          catchError((error) =>
            of(
              DelegationActions.loadDelegationByIdFailure({
                error: error?.error?.message || error?.message || 'Failed to load delegation',
              })
            )
          )
        )
      )
    )
  );

  loadDelegationByIdFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DelegationActions.loadDelegationByIdFailure),
        tap(({ error }) => {
          this.toastService.showError('Error', error);
        })
      ),
    { dispatch: false }
  );
}
